import { useAuthenticator } from '@aws-amplify/ui-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ShellPersona } from '../app-shell/moduleRegistry';
import {
  ensurePersonaTokenClaim,
  provisionActiveSessionPersona,
} from '../auth/personaProvisioningRuntime';
import {
  describeClaimRefreshFailure,
  normalizePersonaProvisioningFailureReason,
  resolvePersonaProvisioningCompletion,
} from '../auth/personaProvisioningOutcome';
import {
  clearPersonaSignupIntentForEmail,
  readPersonaSignupIntent,
  readPersonaSignupIntentForEmail,
  type PersonaSignupIntent,
} from '../auth/personaProvisioningStorage';
import {
  areCognitoProfileResolutionsEquivalent,
  buildCognitoProfileResolutionSignature,
  clearStaleCognitoTokenFamilies,
  resolveCognitoProfileResolution,
  type CognitoFailureReason,
  type CognitoObservedTokenTuple,
  type CognitoProfile,
} from '../utils/cognito-profile';
import type { YstnjAuthAuthority } from '../auth/ystnjAuthAuthority';
import { isYstnjSuperAdminEmail } from '../../config/superAdminIdentityPolicy';
import { resolvePostAuthPersonaSelectionMode } from '../config/publicAuth';
import { useAppSelector } from './hooks';

const STORAGE_PERSONA_KEYS = ['ystnj_persona', 'triedge_persona'];
type PersonaProvisioningStatus =
  | 'idle'
  | 'selection_required'
  | 'in_progress'
  | 'complete'
  | 'failed';

type PersonaProvisioningOperationStatus =
  | 'not_started'
  | 'skipped'
  | 'success'
  | 'failed';

export type PersonaProvisioningDiagnostics = {
  status: PersonaProvisioningStatus;
  provider: string | null;
  email: string | null;
  username: string | null;
  role: string | null;
  permissions: string[];
  requestedPersona: string | null;
  requestedAccountType: string | null;
  intentEmail: string | null;
  intentCreatedAt: string | null;
  staffSignupCodePresent: boolean | null;
  schoolId: string | null;
  backendStatus: string | null;
  backendPersona: string | null;
  backendAssignedGroup: string | null;
  backendPendingApproval: boolean | null;
  backendReason: string | null;
  backendObservedPayloadType: string | null;
  backendObservedPayloadKeys: string[];
  operation: 'provisionSelfPersona' | null;
  operationStatus: PersonaProvisioningOperationStatus;
  operationErrorClass: string | null;
  operationErrorMessage: string | null;
  tokenRefreshTargetPersona: string | null;
  tokenRefreshReady: boolean | null;
  tokenRefreshReason: string | null;
  tokenRefreshAttempts: number | null;
  tokenGroups: string[];
  autoUpgradeCandidate: boolean | null;
  autoUpgradeDecision: string | null;
  failureReason: string | null;
};

const PERSONA_GROUP_TOKENS = new Set([
  'parent',
  'staff',
  'editor',
  'nurse',
  'admin',
  'administrator',
  'owner',
  'super-administrator',
]);

const normalizeGroupToken = (value: string): string =>
  value.trim().toLowerCase();

const FORCE_SETTINGS_SUPER_ADMIN_SESSION_KEY = 'ystnj.dev.forceSettingsSuperAdmin';
const FORCE_CONTROL_PLANE_SUPER_ADMIN_SESSION_KEY =
  'ystnj.dev.forceControlPlaneSuperAdmin';

const isTruthyDevFlag = (value: string | null): boolean =>
  value === '1' || value === 'true' || value === 'yes';

const isFalsyDevFlag = (value: string | null): boolean =>
  value === '0' || value === 'false' || value === 'no';

const resolveDevPersonaOverride = (): ShellPersona | null => {
  if (!import.meta.env.DEV || typeof window === 'undefined') {
    return null;
  }

  const { pathname, search } = window.location;
  const settingsPathActive = pathname === '/school/settings'
    || pathname.startsWith('/school/settings/');
  const controlPlanePathActive = pathname === '/super-administrator/control-plane'
    || pathname.startsWith('/super-administrator/control-plane/');
  const params = new URLSearchParams(search);
  const settingsFlag = params.get('forceSuperAdminSettings');
  const controlPlaneFlag = params.get('forceSuperAdminControlPlane');

  try {
    if (isTruthyDevFlag(settingsFlag) && settingsPathActive) {
      window.sessionStorage.setItem(FORCE_SETTINGS_SUPER_ADMIN_SESSION_KEY, '1');
    }
    if (isFalsyDevFlag(settingsFlag)) {
      window.sessionStorage.removeItem(FORCE_SETTINGS_SUPER_ADMIN_SESSION_KEY);
    }

    if (isTruthyDevFlag(controlPlaneFlag) && controlPlanePathActive) {
      window.sessionStorage.setItem(
        FORCE_CONTROL_PLANE_SUPER_ADMIN_SESSION_KEY,
        '1',
      );
    }
    if (isFalsyDevFlag(controlPlaneFlag)) {
      window.sessionStorage.removeItem(
        FORCE_CONTROL_PLANE_SUPER_ADMIN_SESSION_KEY,
      );
    }

    const shouldForceSettingsSuperAdmin = settingsPathActive
      && window.sessionStorage.getItem(FORCE_SETTINGS_SUPER_ADMIN_SESSION_KEY) === '1';
    const shouldForceControlPlaneSuperAdmin = controlPlanePathActive
      && window.sessionStorage.getItem(FORCE_CONTROL_PLANE_SUPER_ADMIN_SESSION_KEY) === '1';

    return shouldForceSettingsSuperAdmin || shouldForceControlPlaneSuperAdmin
      ? 'super-administrator'
      : null;
  } catch {
    return null;
  }
};

const hasPersonaGrantGroup = (groups: string[] | undefined): boolean =>
  (groups ?? []).some((group) => PERSONA_GROUP_TOKENS.has(normalizeGroupToken(group)));

const hasAnyNormalizedGroup = (
  groups: string[] | undefined,
  allowed: readonly string[],
): boolean => {
  const allowedSet = new Set(allowed.map((group) => normalizeGroupToken(group)));
  return (groups ?? []).some((group) => allowedSet.has(normalizeGroupToken(group)));
};

const hasParentGrantGroup = (groups: string[] | undefined): boolean =>
  hasAnyNormalizedGroup(groups, ['parent']);

const hasSuperAdminGrantGroup = (groups: string[] | undefined): boolean =>
  hasAnyNormalizedGroup(groups, ['super-administrator']);

const isSignupIntentSatisfiedByGroups = (
  intent: PersonaSignupIntent,
  groups: string[] | undefined,
): boolean => {
  const requested = intent.requestedAccountType ?? intent.requestedPersona;
  if (requested === 'parent') {
    return hasAnyNormalizedGroup(groups, ['parent']);
  }
  if (requested === 'staff') {
    return hasAnyNormalizedGroup(groups, [
      'staff',
      'editor',
      'nurse',
      'admin',
      'administrator',
      'owner',
      'super-administrator',
    ]);
  }
  if (requested === 'administrator') {
    return hasAnyNormalizedGroup(groups, [
      'admin',
      'administrator',
      'owner',
      'super-administrator',
    ]);
  }
  return false;
};

const resolveAutoUpgradeDecision = ({
  profile,
  groups,
  origin,
}: {
  profile: CognitoProfile | null;
  groups: string[] | undefined;
  origin: string | null | undefined;
}): {
  candidate: boolean;
  decision: string;
} => {
  if (!profile?.isGoogleProvider) {
    return { candidate: false, decision: 'not-google-provider' };
  }
  if (!profile.emailVerified) {
    return { candidate: false, decision: 'email-not-verified' };
  }
  if (!profile.isIntellioUser) {
    return { candidate: false, decision: 'not-intelliolabs-account' };
  }
  if (!isYstnjSuperAdminEmail({ email: profile.email, origin })) {
    return { candidate: false, decision: 'not-super-administrator-allowlisted' };
  }
  if (profile.isSuperAdmin || hasSuperAdminGrantGroup(groups)) {
    return { candidate: false, decision: 'already-super-administrator' };
  }
  if (hasParentGrantGroup(groups)) {
    return { candidate: false, decision: 'parent-session-blocked' };
  }
  return { candidate: true, decision: 'eligible-google-intellio-session' };
};

const mapPersona = (profile: CognitoProfile | null): ShellPersona => {
  if (!profile) return 'guest';
  if (profile.isSuperAdmin) return 'super-administrator';
  if (profile.persona === 'super-administrator') return 'super-administrator';
  if (profile.persona === 'administrator') return 'administrator';
  if (profile.persona === 'staff') return 'staff';
  if (profile.persona === 'parent') return 'parent';
  return 'guest';
};

const describeResolutionFailure = (
  reason: CognitoFailureReason | null,
): string | null => {
  if (reason === 'token-authority-mismatch') {
    return 'The stored Cognito session does not match the configured YSTNJ auth tuple.';
  }
  if (reason === 'no-authority-matched-token') {
    return 'No local Cognito token matched the configured YSTNJ auth tuple.';
  }
  if (reason === 'token-decode-failed') {
    return 'The stored Cognito token could not be decoded for this YSTNJ session.';
  }
  return null;
};

const formatIntentCreatedAt = (intent: PersonaSignupIntent | null): string | null => {
  if (!intent?.createdAt) {
    return null;
  }
  const date = new Date(intent.createdAt);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const buildPersonaProvisioningDiagnostics = ({
  status,
  profile,
  intent,
  backendResult = null,
  operationStatus = 'not_started',
  operationError = null,
  tokenRefreshResult = null,
  tokenGroups = profile?.groups ?? [],
  autoUpgradeCandidate = null,
  autoUpgradeDecision = null,
  failureReason = null,
}: {
  status: PersonaProvisioningStatus;
  profile: CognitoProfile | null;
  intent: PersonaSignupIntent | null;
  backendResult?: Awaited<ReturnType<typeof provisionActiveSessionPersona>>;
  operationStatus?: PersonaProvisioningOperationStatus;
  operationError?: unknown;
  tokenRefreshResult?: Awaited<ReturnType<typeof ensurePersonaTokenClaim>> | null;
  tokenGroups?: string[];
  autoUpgradeCandidate?: boolean | null;
  autoUpgradeDecision?: string | null;
  failureReason?: string | null;
}): PersonaProvisioningDiagnostics => {
  const error = operationError instanceof Error ? operationError : null;
  return {
    status,
    provider: profile?.provider ?? null,
    email: profile?.email ?? null,
    username: profile?.username ?? null,
    role: profile?.role ?? null,
    permissions: profile?.permissions ?? [],
    requestedPersona: intent?.requestedPersona ?? null,
    requestedAccountType: intent?.requestedAccountType ?? null,
    intentEmail: intent?.email ?? null,
    intentCreatedAt: formatIntentCreatedAt(intent),
    staffSignupCodePresent: intent ? Boolean(intent.staffSignupCode) : null,
    schoolId: intent?.schoolId ?? null,
    backendStatus: backendResult?.status ?? null,
    backendPersona: backendResult?.persona ?? null,
    backendAssignedGroup: backendResult?.assignedGroup ?? null,
    backendPendingApproval: backendResult ? backendResult.pendingApproval : null,
    backendReason: backendResult?.reason ?? null,
    backendObservedPayloadType: backendResult?.observedPayloadType ?? null,
    backendObservedPayloadKeys: backendResult?.observedPayloadKeys ?? [],
    operation: operationStatus === 'not_started' ? null : 'provisionSelfPersona',
    operationStatus,
    operationErrorClass: error?.name ?? (operationError ? typeof operationError : null),
    operationErrorMessage: error?.message ?? (operationError ? String(operationError) : null),
    tokenRefreshTargetPersona: tokenRefreshResult?.targetPersona ?? null,
    tokenRefreshReady: tokenRefreshResult ? tokenRefreshResult.ready : null,
    tokenRefreshReason: tokenRefreshResult?.reason ?? null,
    tokenRefreshAttempts: tokenRefreshResult?.attempts ?? null,
    tokenGroups,
    autoUpgradeCandidate,
    autoUpgradeDecision,
    failureReason,
  };
};

const emitAuthSessionUpdated = () => {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new Event('ystnj-auth-session-updated'));
};

export const useShellProfile = () => {
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const personaOverride = useAppSelector((state) => state.shell.personaOverride);
  const [profileResolution, setProfileResolution] = useState(() =>
    resolveCognitoProfileResolution(),
  );
  const [personaProvisioningStatus, setPersonaProvisioningStatus] =
    useState<PersonaProvisioningStatus>('idle');
  const [staffAccessPendingApproval, setStaffAccessPendingApproval] = useState(false);
  const [personaTokenReady, setPersonaTokenReady] = useState(false);
  const [personaTokenGroups, setPersonaTokenGroups] = useState<string[]>([]);
  const [personaProvisioningError, setPersonaProvisioningError] = useState<string | null>(
    () => describeResolutionFailure(resolveCognitoProfileResolution().failureReason),
  );
  const [failureReason, setFailureReason] = useState<string | null>(
    () => resolveCognitoProfileResolution().failureReason,
  );
  const [personaProvisioningDiagnostics, setPersonaProvisioningDiagnostics] =
    useState<PersonaProvisioningDiagnostics | null>(null);
  const provisioningAttemptRef = useRef<Set<string>>(new Set());
  const mismatchCleanupRef = useRef<Set<string>>(new Set());
  const commitProfileResolution = (nextResolution: ReturnType<typeof resolveCognitoProfileResolution>) => {
    setProfileResolution((currentResolution) => (
      areCognitoProfileResolutionsEquivalent(currentResolution, nextResolution)
        ? currentResolution
        : nextResolution
    ));
  };

  useEffect(() => {
    if (typeof window === 'undefined') return () => {};
    let attempts = 0;
    const refresh = () => {
      attempts += 1;
      const nextResolution = resolveCognitoProfileResolution();
      commitProfileResolution(nextResolution);
      if (
        (nextResolution.profile?.email && nextResolution.profile?.userSub)
        || attempts >= 6
      ) {
        window.clearInterval(intervalId);
      }
    };
    const intervalId = window.setInterval(refresh, 1500);
    refresh();

    const handleStorage = (event: StorageEvent) => {
      if (!event.key) return;
      if (
        event.key.includes('CognitoIdentityServiceProvider')
        || event.key.endsWith('.idToken')
        || event.key.includes('triedge_persona')
      ) {
        refresh();
      }
    };
    const handleSessionUpdated = () => {
      refresh();
    };
    window.addEventListener('storage', handleStorage);
    window.addEventListener('ystnj-auth-session-updated', handleSessionUpdated);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('ystnj-auth-session-updated', handleSessionUpdated);
    };
  }, []);

  const profile = profileResolution.profile;
  const profileResolutionSignature = useMemo(
    () => buildCognitoProfileResolutionSignature(profileResolution),
    [profileResolution],
  );
  const expectedOrigin = profileResolution.expectedTuple?.origin;
  const basePersona = useMemo(() => mapPersona(profile), [profile]);
  const personaSelectionMode = resolvePostAuthPersonaSelectionMode({
    isGoogleProvider: profile?.isGoogleProvider,
    isIntellioUser: profile?.isIntellioUser,
    groups: profile?.groups,
  });
  const allowOverride = Boolean(
    profile?.isGoogleProvider
    && (profile?.isIntellioUser || personaSelectionMode === 'granted-groups'),
  );
  const devPersonaOverride = resolveDevPersonaOverride();
  const standardEffectivePersona = useMemo(() => {
    if (allowOverride && personaOverride) {
      return personaOverride;
    }
    return basePersona;
  }, [allowOverride, personaOverride, basePersona]);
  const effectivePersona = useMemo(() => {
    if (
      devPersonaOverride
      && (
        standardEffectivePersona === 'administrator'
        || standardEffectivePersona === 'super-administrator'
      )
    ) {
      return devPersonaOverride;
    }
    return standardEffectivePersona;
  }, [devPersonaOverride, standardEffectivePersona]);
  const requiresPrivilegedAuthorityReconciliation = Boolean(
    hasSuperAdminGrantGroup(profile?.groups)
      && !isYstnjSuperAdminEmail({
        email: profile?.email,
        origin: expectedOrigin,
      }),
  );
  const exposedEffectivePersona =
    staffAccessPendingApproval || requiresPrivilegedAuthorityReconciliation
      ? 'guest'
      : effectivePersona;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (authStatus !== 'authenticated' || exposedEffectivePersona === 'guest') {
      STORAGE_PERSONA_KEYS.forEach((key) => window.localStorage.removeItem(key));
      return;
    }
    STORAGE_PERSONA_KEYS.forEach((key) => {
      window.localStorage.setItem(key, exposedEffectivePersona);
    });
  }, [authStatus, exposedEffectivePersona]);

  useEffect(() => {
    if (authStatus !== 'authenticated') {
      STORAGE_PERSONA_KEYS.forEach((key) => window.localStorage.removeItem(key));
      provisioningAttemptRef.current.clear();
      mismatchCleanupRef.current.clear();
      setPersonaProvisioningStatus('idle');
      setStaffAccessPendingApproval(false);
      setPersonaTokenReady(false);
      setPersonaTokenGroups([]);
      setPersonaProvisioningError(null);
      setFailureReason(null);
      setPersonaProvisioningDiagnostics(null);
      return;
    }

    if (profileResolution.failureReason) {
      if (!mismatchCleanupRef.current.has(profileResolution.failureReason)) {
        clearStaleCognitoTokenFamilies();
        mismatchCleanupRef.current.add(profileResolution.failureReason);
      }
      setPersonaProvisioningStatus('failed');
      setStaffAccessPendingApproval(false);
      setPersonaTokenReady(false);
      setPersonaTokenGroups(profile?.groups ?? []);
      setPersonaProvisioningError(
        describeResolutionFailure(profileResolution.failureReason),
      );
      setFailureReason(profileResolution.failureReason);
      setPersonaProvisioningDiagnostics(buildPersonaProvisioningDiagnostics({
        status: 'failed',
        profile,
        intent: null,
        operationStatus: 'skipped',
        tokenGroups: profile?.groups ?? [],
        failureReason: profileResolution.failureReason,
      }));
      return;
    }

    mismatchCleanupRef.current.clear();
    setPersonaProvisioningError(null);
    setFailureReason(null);

    const email = profile?.email?.trim().toLowerCase() || '';
    const userSub = profile?.userSub?.trim() || '';
    const pendingIntent = email
      ? readPersonaSignupIntentForEmail(email)
      : readPersonaSignupIntent();
    const hasIdentity = Boolean(email || userSub);
    const hasPersonaGroup = hasPersonaGrantGroup(profile?.groups);
    const hasViewerOnlyGroup =
      hasAnyNormalizedGroup(profile?.groups, ['viewer'])
      && !hasPersonaGroup;
    const hasPendingStaffSignupIntent =
      pendingIntent?.requestedAccountType === 'staff'
      || pendingIntent?.requestedPersona === 'staff';
    const autoUpgrade = resolveAutoUpgradeDecision({
      profile,
      groups: profile?.groups,
      origin: expectedOrigin,
    });
    const pendingIntentRequiresProvisioning = Boolean(
      pendingIntent
      && (
        hasPendingStaffSignupIntent
        || !isSignupIntentSatisfiedByGroups(pendingIntent, profile?.groups)
      ),
    );
    if (!hasIdentity) {
      setPersonaTokenReady(false);
      setPersonaTokenGroups([]);
      setPersonaProvisioningDiagnostics(buildPersonaProvisioningDiagnostics({
        status: 'idle',
        profile,
        intent: pendingIntent,
        operationStatus: 'skipped',
        tokenGroups: [],
        autoUpgradeCandidate: autoUpgrade.candidate,
        autoUpgradeDecision: autoUpgrade.decision,
      }));
      return;
    }

    if (
      hasPersonaGroup
      && !autoUpgrade.candidate
      && !pendingIntentRequiresProvisioning
      && !requiresPrivilegedAuthorityReconciliation
    ) {
      if (email) {
        clearPersonaSignupIntentForEmail(email);
      }
      setPersonaProvisioningStatus('complete');
      setStaffAccessPendingApproval(false);
      setPersonaTokenReady(true);
      setPersonaTokenGroups(profile?.groups ?? []);
      setPersonaProvisioningError(null);
      setFailureReason(null);
      setPersonaProvisioningDiagnostics(buildPersonaProvisioningDiagnostics({
        status: 'complete',
        profile,
        intent: pendingIntent,
        operationStatus: 'skipped',
        tokenGroups: profile?.groups ?? [],
        autoUpgradeCandidate: autoUpgrade.candidate,
        autoUpgradeDecision: autoUpgrade.decision,
      }));
      return;
    }

    const shouldAutoProvisionGoogleSuperAdmin = autoUpgrade.candidate;
    const isOpportunisticViewerProbe = Boolean(
      hasViewerOnlyGroup
      && !pendingIntent
      && !shouldAutoProvisionGoogleSuperAdmin
      && profile?.isGoogleProvider,
    );
    const defaultGoogleParentIntent: PersonaSignupIntent | null = isOpportunisticViewerProbe
      ? {
          version: 1,
          email,
          requestedPersona: 'parent',
          createdAt: Date.now(),
        }
      : null;
    const activeProvisioningIntent = pendingIntent ?? defaultGoogleParentIntent;
    const isDefaultGoogleParentProbe = Boolean(defaultGoogleParentIntent);

    if (
      profile?.isGoogleProvider
      && !shouldAutoProvisionGoogleSuperAdmin
      && !pendingIntent
      && !hasViewerOnlyGroup
      && !requiresPrivilegedAuthorityReconciliation
    ) {
      setPersonaProvisioningStatus('selection_required');
      setPersonaTokenReady(false);
      setPersonaTokenGroups(profile?.groups ?? []);
      setPersonaProvisioningError(null);
      setFailureReason(null);
      setPersonaProvisioningDiagnostics(buildPersonaProvisioningDiagnostics({
        status: 'selection_required',
        profile,
        intent: pendingIntent,
        operationStatus: 'skipped',
        tokenGroups: profile?.groups ?? [],
        autoUpgradeCandidate: autoUpgrade.candidate,
        autoUpgradeDecision: autoUpgrade.decision,
      }));
      return;
    }

    if (
      hasViewerOnlyGroup
      && !pendingIntent
      && !shouldAutoProvisionGoogleSuperAdmin
      && !profile?.isGoogleProvider
    ) {
      if (email) {
        clearPersonaSignupIntentForEmail(email);
      }
      setPersonaProvisioningStatus('complete');
      setStaffAccessPendingApproval(false);
      setPersonaTokenReady(true);
      setPersonaTokenGroups(profile?.groups ?? []);
      setPersonaProvisioningError(null);
      setFailureReason(null);
      setPersonaProvisioningDiagnostics(buildPersonaProvisioningDiagnostics({
        status: 'complete',
        profile,
        intent: pendingIntent,
        operationStatus: 'skipped',
        tokenGroups: profile?.groups ?? [],
        autoUpgradeCandidate: autoUpgrade.candidate,
        autoUpgradeDecision: autoUpgrade.decision,
      }));
      return;
    }

    const attemptKey = `${userSub}|${email}|${
      shouldAutoProvisionGoogleSuperAdmin
        ? 'google-super-administrator'
        : requiresPrivilegedAuthorityReconciliation
          ? 'privileged-authority-reconciliation'
          : 'session'
    }`;
    if (provisioningAttemptRef.current.has(attemptKey)) {
      return;
    }
    provisioningAttemptRef.current.add(attemptKey);
    setPersonaProvisioningStatus('in_progress');
    setStaffAccessPendingApproval(false);
    setPersonaTokenReady(false);
    setPersonaTokenGroups(profile?.groups ?? []);
    setPersonaProvisioningError(null);
    setFailureReason(null);
    setPersonaProvisioningDiagnostics(buildPersonaProvisioningDiagnostics({
      status: 'in_progress',
      profile,
      intent: pendingIntent,
      operationStatus: 'not_started',
      tokenGroups: profile?.groups ?? [],
      autoUpgradeCandidate: autoUpgrade.candidate,
      autoUpgradeDecision: autoUpgrade.decision,
    }));

    const pendingIntentInput = activeProvisioningIntent && !isDefaultGoogleParentProbe
      ? {
          requestedPersona: activeProvisioningIntent.requestedPersona,
          requestedAccountType: activeProvisioningIntent.requestedAccountType,
          staffSignupCode: activeProvisioningIntent.staffSignupCode,
          schoolId: activeProvisioningIntent.schoolId,
          intentEmail: activeProvisioningIntent.financialAidInvite ? activeProvisioningIntent.email : undefined,
          financialAidInvite: activeProvisioningIntent.financialAidInvite,
        }
      : {};

    void provisionActiveSessionPersona({
      emailHint: email || undefined,
      ...pendingIntentInput,
      requestedPersona: shouldAutoProvisionGoogleSuperAdmin
        ? 'administrator'
        : isDefaultGoogleParentProbe
          ? undefined
          : activeProvisioningIntent?.requestedPersona,
      source: shouldAutoProvisionGoogleSuperAdmin
        ? 'google-super-administrator-auto-provision'
        : requiresPrivilegedAuthorityReconciliation
          ? 'privileged-authority-reconciliation'
          : 'session-refresh',
    })
      .then(async (result) => {
        if (result?.status === 'pending_staff_approval' || result?.pendingApproval) {
          setPersonaProvisioningStatus('complete');
          setStaffAccessPendingApproval(true);
          setPersonaTokenReady(true);
          setPersonaTokenGroups(profile?.groups ?? []);
          setPersonaProvisioningError(null);
          setFailureReason(null);
          setPersonaProvisioningDiagnostics(buildPersonaProvisioningDiagnostics({
            status: 'complete',
            profile,
            intent: activeProvisioningIntent,
            backendResult: result,
            operationStatus: 'success',
            tokenGroups: profile?.groups ?? [],
            autoUpgradeCandidate: autoUpgrade.candidate,
            autoUpgradeDecision: autoUpgrade.decision,
            failureReason: result?.reason ?? 'staff_signup_pending_approval',
          }));
          return;
        }

        setStaffAccessPendingApproval(false);
        const completion = resolvePersonaProvisioningCompletion(result, {
          hasPersonaIntent: Boolean(activeProvisioningIntent) || shouldAutoProvisionGoogleSuperAdmin,
          isFederatedProvider: Boolean(profile?.isGoogleProvider),
        });
        if (completion.kind === 'failure') {
          const concreteFailureReason =
            normalizePersonaProvisioningFailureReason(completion.failureReason);
          setPersonaProvisioningStatus('failed');
          setPersonaProvisioningError(completion.message);
          setFailureReason(concreteFailureReason);
          setPersonaTokenReady(false);
          setPersonaProvisioningDiagnostics(buildPersonaProvisioningDiagnostics({
            status: 'failed',
            profile,
            intent: activeProvisioningIntent,
            backendResult: result,
            operationStatus: 'success',
            tokenGroups: profile?.groups ?? [],
            autoUpgradeCandidate: autoUpgrade.candidate,
            autoUpgradeDecision: autoUpgrade.decision,
            failureReason: concreteFailureReason,
          }));
          return;
        }

        if (
          normalizeGroupToken(completion.targetPersona) === 'viewer'
          && activeProvisioningIntent?.requestedPersona === 'parent'
        ) {
          if (isDefaultGoogleParentProbe) {
            setPersonaProvisioningStatus('selection_required');
            setPersonaProvisioningError(null);
            setFailureReason(null);
            setPersonaTokenReady(false);
            setPersonaProvisioningDiagnostics(buildPersonaProvisioningDiagnostics({
              status: 'selection_required',
              profile,
              intent: activeProvisioningIntent,
              backendResult: result,
              operationStatus: 'success',
              tokenGroups: profile?.groups ?? [],
              autoUpgradeCandidate: autoUpgrade.candidate,
              autoUpgradeDecision: autoUpgrade.decision,
            }));
            return;
          }

          const concreteFailureReason = 'parent-provisioning-returned-viewer';
          setPersonaProvisioningStatus('failed');
          setPersonaProvisioningError(
            'Google sign-in completed, but YSTNJ could not assign parent access for this session.',
          );
          setFailureReason(concreteFailureReason);
          setPersonaTokenReady(false);
          setPersonaProvisioningDiagnostics(buildPersonaProvisioningDiagnostics({
            status: 'failed',
            profile,
            intent: activeProvisioningIntent,
            backendResult: result,
            operationStatus: 'success',
            tokenGroups: profile?.groups ?? [],
            autoUpgradeCandidate: autoUpgrade.candidate,
            autoUpgradeDecision: autoUpgrade.decision,
            failureReason: concreteFailureReason,
          }));
          return;
        }

        const claim = await ensurePersonaTokenClaim(completion.targetPersona, {
          maxAttempts: 6,
          baseDelayMs: 250,
        });
        console.info('[auth] persona_token_refresh_attempt', {
          targetPersona: claim.targetPersona,
          ready: claim.ready,
          attempts: claim.attempts,
          groups: claim.groups,
          reason: claim.reason,
        });
        setPersonaTokenGroups(claim.groups);

        if (!claim.ready) {
          const concreteFailureReason =
            normalizePersonaProvisioningFailureReason(claim.reason);
          setPersonaProvisioningStatus('failed');
          setPersonaProvisioningError(
            describeClaimRefreshFailure(claim.reason),
          );
          setFailureReason(concreteFailureReason);
          setPersonaTokenReady(false);
          setPersonaProvisioningDiagnostics(buildPersonaProvisioningDiagnostics({
            status: 'failed',
            profile,
            intent: activeProvisioningIntent,
            backendResult: result,
            operationStatus: 'success',
            tokenRefreshResult: claim,
            tokenGroups: claim.groups,
            autoUpgradeCandidate: autoUpgrade.candidate,
            autoUpgradeDecision: autoUpgrade.decision,
            failureReason: concreteFailureReason,
          }));
          return;
        }

        emitAuthSessionUpdated();
        setPersonaProvisioningStatus('complete');
        setPersonaTokenReady(true);
        setPersonaProvisioningError(null);
        setFailureReason(null);
        setPersonaProvisioningDiagnostics(buildPersonaProvisioningDiagnostics({
          status: 'complete',
          profile,
          intent: activeProvisioningIntent,
          backendResult: result,
          operationStatus: 'success',
          tokenRefreshResult: claim,
          tokenGroups: claim.groups,
          autoUpgradeCandidate: autoUpgrade.candidate,
          autoUpgradeDecision: autoUpgrade.decision,
        }));
        window.setTimeout(() => {
          commitProfileResolution(resolveCognitoProfileResolution());
        }, 250);
      })
      .catch((error) => {
        const nextError =
          error instanceof Error && error.message.trim().length > 0
            ? error.message
            : 'Persona provisioning failed.';
        console.warn('[auth] persona provisioning failed:', error);
        const concreteFailureReason = 'persona-provisioning-error';
        setPersonaProvisioningStatus('failed');
        setStaffAccessPendingApproval(false);
        setPersonaProvisioningError(nextError);
        setFailureReason(concreteFailureReason);
        setPersonaTokenReady(false);
        setPersonaProvisioningDiagnostics(buildPersonaProvisioningDiagnostics({
          status: 'failed',
          profile,
          intent: activeProvisioningIntent,
          operationStatus: 'failed',
          operationError: error,
          tokenGroups: profile?.groups ?? [],
          autoUpgradeCandidate: autoUpgrade.candidate,
          autoUpgradeDecision: autoUpgrade.decision,
          failureReason: concreteFailureReason,
        }));
      });
  }, [
    authStatus,
    expectedOrigin,
    profileResolutionSignature,
    requiresPrivilegedAuthorityReconciliation,
  ]);

  return {
    profile,
    authStatus,
    basePersona,
    effectivePersona: exposedEffectivePersona,
    allowOverride,
    personaProvisioningStatus,
    staffAccessPendingApproval,
    personaProvisioningError,
    personaTokenReady,
    personaTokenGroups,
    requiresPrivilegedAuthorityReconciliation,
    expectedTuple: profileResolution.expectedTuple as YstnjAuthAuthority,
    observedTokenTuple:
      profileResolution.observedTokenTuple as CognitoObservedTokenTuple | null,
    failureReason,
    personaProvisioningDiagnostics,
  };
};
